import React, { useState, useEffect } from 'react';
import { 
    Table, 
    Input, 
    Space, 
    Tag,
    Image,
    message,
    Typography,
    Form,
    Button,
    Card,
    Row,
    Col,
    Collapse,
    Select,
    Divider,
    Spin
} from 'antd';
import { 
    SearchOutlined,
    ShopOutlined,
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    GlobalOutlined,
    FilterOutlined,
    ReloadOutlined,
    EyeOutlined,
    EnvironmentOutlined,
    IdcardOutlined,
    DollarOutlined,
    LinkOutlined,
    PictureOutlined,
    FileTextOutlined,
    TagOutlined
} from '@ant-design/icons';
import companyService from '../../../services/companyService';
import DashboardLayout from '../../../components/Dashboard/DashboardLayout';
import PageHeader from '../../../components/Common/PageHeader';
import useColumnVisibility from '../../../components/Common/ColumnVisibilityControl';
import CompanyDrawer from '../../../components/Common/CompanyDrawer';
import env from '../../../env';

const { Text, Title } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

const businessTypes = [
    'Individual',
    'Sole Proprietorship',
    'Partnership',
    'Private Limited Company',
    'Public Limited Company',
    'LLC',
    'Corporation',
    'Other'
];

const Companies = () => {
    const [form] = Form.useForm();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [companyDrawerVisible, setCompanyDrawerVisible] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);

    const fetchCompanies = async (page = 1, params = {}) => {
        try {
            setLoading(true);
            const response = await companyService.getAllCompanies({ 
                page,
                ...params
            });
            setCompanies(response.data);
            setPagination(prev => ({
                ...prev,
                current: response.meta.current_page,
                total: response.meta.total,
                pageSize: response.meta.per_page
            }));
        } catch (error) {
            message.error('Failed to fetch companies');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const handleSearch = (values) => {
        // Build search string from all fields
        const searchFields = [];
        if (values.company_name) searchFields.push(values.company_name);
        if (values.owner_name) searchFields.push(values.owner_name);
        if (values.owner_email) searchFields.push(values.owner_email);
        if (values.email) searchFields.push(values.email);
        if (values.phone) searchFields.push(values.phone);
        
        const search = searchFields.join(' ');
        const params = { search };
        
        // Add business type filter if selected
        if (values.business_type) {
            params.business_type = values.business_type;
        }
        
        setSearchParams(params);
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchCompanies(1, params);
    };

    const handleReset = () => {
        form.resetFields();
        setSearchParams({});
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchCompanies(1, {});
    };

    const handleTableChange = (pagination) => {
        fetchCompanies(pagination.current, searchParams);
    };

    const handleViewCompany = (company) => {
        setSelectedCompany(company);
        setCompanyDrawerVisible(true);
    };

    const columns = [
        {
            title: 'Logo',
            dataIndex: 'company_logo_url',
            key: 'logo',
            width: 80,
            render: (logoUrl) => {
                if (!logoUrl) return '-';
                const url = logoUrl.startsWith('http') ? logoUrl : env.BACKEND_URL + logoUrl;
                return (
                    <Image
                        src={url}
                        alt="Company Logo"
                        width={50}
                        height={50}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                        preview={false}
                    />
                );
            }
        },
        {
            title: 'Company Name',
            dataIndex: 'company_name',
            key: 'company_name',
            width: 200,
            render: (text) => text || '-'
        },
        {
            title: 'Business Type',
            dataIndex: 'business_type',
            key: 'business_type',
            width: 150,
            render: (text) => text || '-'
        },
        {
            title: 'Owner',
            key: 'user',
            width: 180,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>
                        <UserOutlined /> {record.user_name || '-'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        <MailOutlined /> {record.user_email || '-'}
                    </Text>
                </Space>
            )
        },
        {
            title: 'Email',
            dataIndex: 'email_address',
            key: 'email',
            width: 180,
            render: (text) => text ? (
                <Space>
                    <MailOutlined />
                    <span>{text}</span>
                </Space>
            ) : '-'
        },
        {
            title: 'Phone / WhatsApp',
            dataIndex: 'phone_whatsapp',
            key: 'phone',
            width: 200,
            render: (text) => text ? (
                <Space>
                    <PhoneOutlined />
                    <span>{text}</span>
                </Space>
            ) : '-'
        },
        {
            title: 'Website',
            dataIndex: 'website',
            key: 'website',
            width: 240,
            render: (text) => text ? (
                <a href={text} target="_blank" rel="noopener noreferrer">
                    <GlobalOutlined /> {text}
                </a>
            ) : '-'
        },
        {
            title: 'Registration #',
            dataIndex: 'company_registration_number',
            key: 'registration',
            width: 150,
            render: (text) => text || '-'
        },
        {
            title: 'Tax/NTN #',
            dataIndex: 'tax_ntn_number',
            key: 'tax_ntn',
            width: 120,
            render: (text) => text || '-'
        },
        {
            title: 'Tagline',
            dataIndex: 'short_tagline',
            key: 'tagline',
            width: 200,
            ellipsis: true,
            render: (text) => text || '-'
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            render: (date) => date ? new Date(date).toLocaleDateString() : '-'
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewCompany(record)}
                >
                    View
                </Button>
            )
        }
    ];

    // Column visibility control
    const { visibleColumns, ColumnVisibilityUI } = useColumnVisibility({
        baseColumns: columns,
        storageKey: 'companiesColumnsVisibility'
    });
    const filteredColumns = visibleColumns;

    return (
        <DashboardLayout>
            <PageHeader
                title="Companies"
                description="View and manage all company profiles"
            />

            <Card style={{ marginBottom: 16 }}>
                <Collapse defaultActiveKey={['1']} ghost>
                    <Panel 
                        header={
                            <Space>
                                <FilterOutlined />
                                <span>Advanced Search</span>
                            </Space>
                        } 
                        key="1"
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSearch}
                        >
                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="company_name"
                                        label="Company Name"
                                    >
                                        <Input 
                                            prefix={<ShopOutlined />}
                                            placeholder="Company Name" 
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="business_type"
                                        label="Business Type"
                                    >
                                        <Select 
                                            placeholder="Select Business Type"
                                            allowClear
                                        >
                                            {businessTypes.map(type => (
                                                <Option key={type} value={type}>{type}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="owner_name"
                                        label="Owner Name"
                                    >
                                        <Input 
                                            prefix={<UserOutlined />}
                                            placeholder="Owner Name" 
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="owner_email"
                                        label="Owner Email"
                                    >
                                        <Input 
                                            prefix={<MailOutlined />}
                                            placeholder="Owner Email" 
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="email"
                                        label="Company Email"
                                    >
                                        <Input 
                                            prefix={<MailOutlined />}
                                            placeholder="Company Email" 
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="phone"
                                        label="Phone / WhatsApp"
                                    >
                                        <Input 
                                            prefix={<PhoneOutlined />}
                                            placeholder="Phone / WhatsApp" 
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item>
                                <Space>
                                    <Button 
                                        type="primary" 
                                        htmlType="submit"
                                        icon={<SearchOutlined />}
                                    >
                                        Search
                                    </Button>
                                    <Button 
                                        onClick={handleReset}
                                        icon={<ReloadOutlined />}
                                    >
                                        Reset
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Panel>
                </Collapse>
            </Card>

            {/* Column Visibility Control */}
            {ColumnVisibilityUI}

            <Table
                columns={filteredColumns}
                dataSource={companies}
                rowKey="id"
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} companies`,
                }}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }}
            />

            {/* Company Drawer */}
            <CompanyDrawer
                visible={companyDrawerVisible}
                onClose={() => {
                    setCompanyDrawerVisible(false);
                    setSelectedCompany(null);
                }}
                companyData={selectedCompany}
            />
        </DashboardLayout>
    );
};

export default Companies;

